/**
 * NoteContainer
 *
 * A wrapper class for interactive musical elements rendered by VexFlow.
 * Each NoteContainer encapsulates a Phaser Container with its own dedicated
 * Graphics object, enabling individual animation, tinting, and interaction.
 *
 * This is used by PhaserVexFlowContext's container routing feature to create
 * discrete, animatable game objects for notes, beams, and other elements.
 */

import Phaser from 'phaser';
import { getColorByCbType } from 'Components/Colors';
import { COLORBLIND_TYPE, ERROR_REC_CLASS } from 'Utils/Constants';

/**
 * Possible states for a note element.
 */
export type NoteState =
  | 'default'
  | 'correct'
  | 'early'
  | 'late'
  | 'missed'
  | 'focused'
  | 'upcoming';

/**
 * Convert hex string color (e.g., "#469f6a") to hex number (e.g., 0x469f6a)
 */
function hexStringToNumber(hexString: string): number {
  // Remove # if present
  const hex = hexString.replace('#', '');
  return parseInt(hex, 16);
}

/**
 * Get color mappings for each note state based on colorblind type.
 * Colors match the app's error recognition colors from Components/Colors.tsx
 */
function getStateColors(
  colorblindType: COLORBLIND_TYPE | null,
): Record<NoteState, { fill: number; alpha: number }> {
  const correctColor = getColorByCbType(ERROR_REC_CLASS.CORRECT, colorblindType);
  const semiErrorColor = getColorByCbType(ERROR_REC_CLASS.SEMI_ERROR, colorblindType);
  const errorColor = getColorByCbType(ERROR_REC_CLASS.ERROR, colorblindType);

  return {
    default: { fill: 0x000000, alpha: 1.0 },
    correct: { fill: hexStringToNumber(correctColor), alpha: 1.0 },
    early: { fill: hexStringToNumber(semiErrorColor), alpha: 1.0 },
    late: { fill: hexStringToNumber(semiErrorColor), alpha: 1.0 },
    missed: { fill: hexStringToNumber(errorColor), alpha: 1.0 },
    focused: { fill: 0x2196f3, alpha: 1.0 }, // Blue - not affected by colorblind mode
    upcoming: { fill: 0x9e9e9e, alpha: 0.7 }, // Gray - not affected by colorblind mode
  };
}

/**
 * Configuration options for creating a NoteContainer.
 */
export interface NoteContainerConfig {
  scene: Phaser.Scene;
  id: string;
  elementType: string;
  x?: number;
  y?: number;
  colorblindType?: COLORBLIND_TYPE | null;
}

/**
 * NoteContainer wraps a Phaser Container with a dedicated Graphics object,
 * providing animation and state management for individual musical elements.
 */
export class NoteContainer {
  /** The Phaser container holding all graphics for this element */
  public readonly container: Phaser.GameObjects.Container;

  /** The dedicated graphics object for VexFlow drawing operations */
  public readonly graphics: Phaser.GameObjects.Graphics;

  /** Unique identifier for this element (from VexFlow's group id) */
  public readonly id: string;

  /** Type of element: "stavenote", "beam", etc. */
  public readonly elementType: string;

  /** Reference to the Phaser scene */
  private scene: Phaser.Scene;

  /** Current state of the note */
  private state: NoteState = 'default';

  /** Colorblind type for accessible colors */
  private colorblindType: COLORBLIND_TYPE | null = null;

  /** Cached bounds of the rendered element */
  private bounds: Phaser.Geom.Rectangle | null = null;

  /** Overlay graphics for color effects (separate from main graphics) */
  private overlay: Phaser.GameObjects.Graphics | null = null;

  /** Active tweens for cleanup */
  private activeTweens: Phaser.Tweens.Tween[] = [];

  /** Graphics object for colored note head overlay */
  private coloredHeadGraphics: Phaser.GameObjects.Graphics | null = null;

  /** Stored fill paths (note heads) */
  private fillPaths: string[] = [];

  /** Stored stroke paths (stems, beams, etc.) */
  private strokePaths: { path: string; lineWidth: number }[] = [];

  /** Original position for reset */
  private originalPosition: { x: number; y: number };

  /** Circle object for pulse/halo animation */
  private pulseObject: Phaser.GameObjects.Arc | null = null;

  /** Tween for the pulse animation */
  private pulseTween: Phaser.Tweens.Tween | null = null;

  /** Images created for evenodd fill (hollow note heads) */
  private evenOddImages: { image: Phaser.GameObjects.Image; textureKey: string }[] = [];

  constructor(config: NoteContainerConfig) {
    this.scene = config.scene;
    this.id = config.id;
    this.elementType = config.elementType;
    this.colorblindType = config.colorblindType ?? null;

    // Create the container at the specified position
    // Note: We don't add to scene.add - parent will add this container
    const initialX = config.x ?? 0;
    const initialY = config.y ?? 0;
    this.container = new Phaser.GameObjects.Container(this.scene, initialX, initialY);
    this.originalPosition = { x: initialX, y: initialY };

    // Create the dedicated graphics object for this element
    // Don't use scene.add - just create it and add to container
    this.graphics = new Phaser.GameObjects.Graphics(this.scene);
    this.container.add(this.graphics);

    // Store the id on the container for debugging
    this.container.setData('noteContainerId', this.id);
    this.container.setData('elementType', this.elementType);
  }

  /**
   * Get the Phaser container for adding to a parent container.
   */
  getContainer(): Phaser.GameObjects.Container {
    return this.container;
  }

  /**
   * Get the Graphics object for VexFlow drawing operations.
   */
  getGraphics(): Phaser.GameObjects.Graphics {
    return this.graphics;
  }

  /**
   * Get the current state of the note.
   */
  getState(): NoteState {
    return this.state;
  }

  /**
   * Set the visual state of the note.
   * Applies the corresponding color to the element.
   */
  setState(state: NoteState, colorblindType?: COLORBLIND_TYPE | null): void {
    this.state = state;
    // Update colorblind type if provided
    if (colorblindType !== undefined) {
      this.colorblindType = colorblindType;
    }
    const stateColors = getStateColors(this.colorblindType);
    const colors = stateColors[state];
    this.applyColorOverlay(colors.fill, colors.alpha);
  }

  /**
   * Set the colorblind type for this container.
   * This will update the colors used for states.
   */
  setColorblindType(colorblindType: COLORBLIND_TYPE | null): void {
    this.colorblindType = colorblindType;
    // Reapply current state with new colorblind type
    const stateColors = getStateColors(this.colorblindType);
    const colors = stateColors[this.state];
    this.applyColorOverlay(colors.fill, colors.alpha);
  }

  /**
   * Apply a color overlay to the element.
   * Uses tint on the graphics or creates an overlay rectangle.
   */
  setColor(color: number, alpha: number = 1.0): void {
    this.applyColorOverlay(color, alpha);
  }

  /**
   * Apply color by drawing colored note heads and stems.
   * Hides all original rendered content to avoid overlay alignment issues.
   * Both fill paths (note heads) and stroke paths (stems) are redrawn.
   */
  private applyColorOverlay(color: number, _alpha: number): void {
    // Clear existing colored graphics
    if (this.coloredHeadGraphics) {
      this.coloredHeadGraphics.destroy();
      this.coloredHeadGraphics = null;
    }

    // Clean up colored evenodd images and their textures
    for (const { image, textureKey } of this.evenOddImages) {
      image.destroy();
      if (this.scene.textures.exists(textureKey)) {
        this.scene.textures.remove(textureKey);
      }
    }
    this.evenOddImages = [];

    if (this.state === 'default') {
      // Show all original children when returning to default state
      this.showOriginalContent();
      return;
    }

    // Hide all original rendered content (graphics + any evenodd images from initial render)
    this.hideOriginalContent();

    // Draw colored note heads and stems
    if (this.fillPaths.length === 0 && this.strokePaths.length === 0) {
      // No paths stored, show original
      this.showOriginalContent();
      return;
    }

    this.coloredHeadGraphics = this.scene.add.graphics();
    this.container.add(this.coloredHeadGraphics);

    // Draw filled paths (note heads and dots)
    for (const pathStr of this.fillPaths) {
      this.drawColoredPath(this.coloredHeadGraphics, pathStr, color);
    }

    // Draw stroked paths (stems)
    for (const strokeData of this.strokePaths) {
      this.drawColoredStrokePath(
        this.coloredHeadGraphics,
        strokeData.path,
        color,
        strokeData.lineWidth,
      );
    }
  }

  /**
   * Hide all original rendered content (for when showing colored version).
   * This includes the main graphics and any evenodd images created during initial VexFlow render.
   */
  private hideOriginalContent(): void {
    // Hide the main graphics object
    this.graphics.setVisible(false);

    // Hide all other children that were part of the original render
    // (evenodd images for hollow notes are added as children during PhaserVexFlowContext.fillWithEvenOdd)
    const children = this.container.getAll();
    for (const child of children) {
      // Skip colored content we're about to add
      if (child === this.coloredHeadGraphics) continue;
      // Skip pulse animation circle
      if (child === this.pulseObject) continue;
      // Hide everything else (original graphics and evenodd images)
      (child as Phaser.GameObjects.GameObject & { setVisible?: (v: boolean) => void }).setVisible?.(
        false,
      );
    }
  }

  /**
   * Show all original rendered content (when returning to default state).
   */
  private showOriginalContent(): void {
    // Show the main graphics object
    this.graphics.setVisible(true);

    // Show all children that were part of the original render
    const children = this.container.getAll();
    for (const child of children) {
      // Don't show destroyed colored content
      if (child === this.coloredHeadGraphics) continue;
      // Show everything else
      (child as Phaser.GameObjects.GameObject & { setVisible?: (v: boolean) => void }).setVisible?.(
        true,
      );
    }
  }

  /**
   * Draw a single path with the specified color.
   * Uses Canvas2D with evenodd fill for compound paths (hollow notes like whole/half notes).
   * Uses Phaser Graphics for simple paths (quarter notes, etc.).
   */
  private drawColoredPath(
    graphics: Phaser.GameObjects.Graphics,
    pathStr: string,
    color: number,
  ): void {
    if (!pathStr || pathStr.length === 0) return;

    // Check if path has multiple subpaths (needs evenodd for holes like whole notes)
    // Multiple M commands = multiple subpaths = potential hole
    const moveCommands = (pathStr.match(/M/gi) || []).length;

    if (moveCommands >= 2) {
      // Use Canvas2D with evenodd fill for compound paths (whole notes, half notes with holes)
      this.drawColoredPathWithEvenOdd(pathStr, color);
    } else {
      // Use native Phaser fill for simple paths (quarter notes, etc.) - crisp rendering
      this.drawColoredPathSimple(graphics, pathStr, color);
    }
  }

  /**
   * Draw a simple (non-compound) path using Phaser Graphics.
   */
  private drawColoredPathSimple(
    graphics: Phaser.GameObjects.Graphics,
    pathStr: string,
    color: number,
  ): void {
    graphics.fillStyle(color, 1);
    graphics.beginPath();

    let currentX = 0,
      currentY = 0;
    let startX = 0,
      startY = 0;

    // Parse SVG path commands
    const regex = /([MLQCZmlqcz])([^MLQCZmlqcz]*)/g;
    let match;

    while ((match = regex.exec(pathStr)) !== null) {
      const cmd = match[1];
      const params = match[2]
        .trim()
        .split(/[\s,]+/)
        .filter((s) => s.length > 0)
        .map(parseFloat);

      switch (cmd) {
        case 'M':
          if (params.length >= 2) {
            currentX = params[0];
            currentY = params[1];
            startX = currentX;
            startY = currentY;
            graphics.moveTo(currentX, currentY);
          }
          break;
        case 'm':
          if (params.length >= 2) {
            currentX += params[0];
            currentY += params[1];
            startX = currentX;
            startY = currentY;
            graphics.moveTo(currentX, currentY);
          }
          break;
        case 'L':
          if (params.length >= 2) {
            currentX = params[0];
            currentY = params[1];
            graphics.lineTo(currentX, currentY);
          }
          break;
        case 'l':
          if (params.length >= 2) {
            currentX += params[0];
            currentY += params[1];
            graphics.lineTo(currentX, currentY);
          }
          break;
        case 'Q':
          if (params.length >= 4) {
            const cp1x = params[0];
            const cp1y = params[1];
            const endX = params[2];
            const endY = params[3];
            // Convert quadratic to cubic and approximate
            const cx1 = currentX + (2 / 3) * (cp1x - currentX);
            const cy1 = currentY + (2 / 3) * (cp1y - currentY);
            const cx2 = endX + (2 / 3) * (cp1x - endX);
            const cy2 = endY + (2 / 3) * (cp1y - endY);
            this.approximateBezierToGraphics(
              graphics,
              currentX,
              currentY,
              cx1,
              cy1,
              cx2,
              cy2,
              endX,
              endY,
            );
            currentX = endX;
            currentY = endY;
          }
          break;
        case 'q':
          if (params.length >= 4) {
            const cp1x = currentX + params[0];
            const cp1y = currentY + params[1];
            const endX = currentX + params[2];
            const endY = currentY + params[3];
            const cx1 = currentX + (2 / 3) * (cp1x - currentX);
            const cy1 = currentY + (2 / 3) * (cp1y - currentY);
            const cx2 = endX + (2 / 3) * (cp1x - endX);
            const cy2 = endY + (2 / 3) * (cp1y - endY);
            this.approximateBezierToGraphics(
              graphics,
              currentX,
              currentY,
              cx1,
              cy1,
              cx2,
              cy2,
              endX,
              endY,
            );
            currentX = endX;
            currentY = endY;
          }
          break;
        case 'C':
          if (params.length >= 6) {
            this.approximateBezierToGraphics(
              graphics,
              currentX,
              currentY,
              params[0],
              params[1],
              params[2],
              params[3],
              params[4],
              params[5],
            );
            currentX = params[4];
            currentY = params[5];
          }
          break;
        case 'c':
          if (params.length >= 6) {
            this.approximateBezierToGraphics(
              graphics,
              currentX,
              currentY,
              currentX + params[0],
              currentY + params[1],
              currentX + params[2],
              currentY + params[3],
              currentX + params[4],
              currentY + params[5],
            );
            currentX += params[4];
            currentY += params[5];
          }
          break;
        case 'Z':
        case 'z':
          graphics.closePath();
          currentX = startX;
          currentY = startY;
          break;
      }
    }

    graphics.fillPath();
  }

  /**
   * Draw a compound path (with holes) using Canvas2D with evenodd fill rule.
   * This is necessary for whole notes and half notes that have an inner hole.
   */
  private drawColoredPathWithEvenOdd(pathStr: string, color: number): void {
    // Create a temporary canvas to render the path with evenodd
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      // Fallback: just skip - original will show through
      return;
    }

    // Use device pixel ratio for crisp rendering on high-DPI displays
    const dpr = window.devicePixelRatio || 1;

    // Parse the SVG-style path to get bounds for canvas sizing
    const pathBounds = this.getPathBounds(pathStr);
    const padding = 2;
    const logicalWidth = pathBounds.width + padding * 2;
    const logicalHeight = pathBounds.height + padding * 2;

    // Set canvas size accounting for device pixel ratio
    canvas.width = Math.ceil(logicalWidth * dpr);
    canvas.height = Math.ceil(logicalHeight * dpr);
    canvas.style.width = `${logicalWidth}px`;
    canvas.style.height = `${logicalHeight}px`;

    // Scale context for high-DPI rendering
    ctx.scale(dpr, dpr);

    // Set fill color (convert hex to CSS color)
    const r = (color >> 16) & 0xff;
    const g = (color >> 8) & 0xff;
    const b = color & 0xff;
    ctx.fillStyle = `rgba(${r}, ${g}, ${b}, 1)`;

    // Replay the path commands on the canvas context
    ctx.beginPath();
    this.replayPathToContext(ctx, pathStr, -pathBounds.x + padding, -pathBounds.y + padding);
    ctx.fill('evenodd');

    // Create a Phaser image from the canvas and add it to the container
    const textureKey = `notecolor_${this.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    this.scene.textures.addCanvas(textureKey, canvas);
    const image = this.scene.add.image(pathBounds.x - padding, pathBounds.y - padding, textureKey);
    image.setOrigin(0, 0);
    // Scale down the image to account for the high-DPI canvas
    image.setScale(1 / dpr);

    // Add to the container (will be cleaned up when coloredHeadGraphics is destroyed)
    this.container.add(image);

    // Track for cleanup
    this.evenOddImages.push({ image, textureKey });
  }

  /**
   * Replay path commands to a Canvas2D context.
   */
  private replayPathToContext(
    ctx: CanvasRenderingContext2D,
    pathStr: string,
    offsetX: number,
    offsetY: number,
  ): void {
    let currentX = 0,
      currentY = 0;

    const regex = /([MLQCZmlqcz])([^MLQCZmlqcz]*)/g;
    let match;

    while ((match = regex.exec(pathStr)) !== null) {
      const cmd = match[1];
      const params = match[2]
        .trim()
        .split(/[\s,]+/)
        .filter((s) => s.length > 0)
        .map(parseFloat);

      switch (cmd) {
        case 'M':
          if (params.length >= 2) {
            currentX = params[0];
            currentY = params[1];
            ctx.moveTo(currentX + offsetX, currentY + offsetY);
          }
          break;
        case 'm':
          if (params.length >= 2) {
            currentX += params[0];
            currentY += params[1];
            ctx.moveTo(currentX + offsetX, currentY + offsetY);
          }
          break;
        case 'L':
          if (params.length >= 2) {
            currentX = params[0];
            currentY = params[1];
            ctx.lineTo(currentX + offsetX, currentY + offsetY);
          }
          break;
        case 'l':
          if (params.length >= 2) {
            currentX += params[0];
            currentY += params[1];
            ctx.lineTo(currentX + offsetX, currentY + offsetY);
          }
          break;
        case 'Q':
          if (params.length >= 4) {
            ctx.quadraticCurveTo(
              params[0] + offsetX,
              params[1] + offsetY,
              params[2] + offsetX,
              params[3] + offsetY,
            );
            currentX = params[2];
            currentY = params[3];
          }
          break;
        case 'q':
          if (params.length >= 4) {
            ctx.quadraticCurveTo(
              currentX + params[0] + offsetX,
              currentY + params[1] + offsetY,
              currentX + params[2] + offsetX,
              currentY + params[3] + offsetY,
            );
            currentX += params[2];
            currentY += params[3];
          }
          break;
        case 'C':
          if (params.length >= 6) {
            ctx.bezierCurveTo(
              params[0] + offsetX,
              params[1] + offsetY,
              params[2] + offsetX,
              params[3] + offsetY,
              params[4] + offsetX,
              params[5] + offsetY,
            );
            currentX = params[4];
            currentY = params[5];
          }
          break;
        case 'c':
          if (params.length >= 6) {
            ctx.bezierCurveTo(
              currentX + params[0] + offsetX,
              currentY + params[1] + offsetY,
              currentX + params[2] + offsetX,
              currentY + params[3] + offsetY,
              currentX + params[4] + offsetX,
              currentY + params[5] + offsetY,
            );
            currentX += params[4];
            currentY += params[5];
          }
          break;
        case 'Z':
        case 'z':
          ctx.closePath();
          break;
      }
    }
  }

  /**
   * Approximate a cubic bezier curve with line segments.
   * Same approach as PhaserVexFlowContext.approximateBezier.
   */
  private approximateBezierToGraphics(
    graphics: Phaser.GameObjects.Graphics,
    startX: number,
    startY: number,
    cp1x: number,
    cp1y: number,
    cp2x: number,
    cp2y: number,
    endX: number,
    endY: number,
    segments: number = 20,
  ): void {
    for (let i = 1; i <= segments; i++) {
      const t = i / segments;
      const t2 = t * t;
      const t3 = t2 * t;
      const mt = 1 - t;
      const mt2 = mt * mt;
      const mt3 = mt2 * mt;

      const px = mt3 * startX + 3 * mt2 * t * cp1x + 3 * mt * t2 * cp2x + t3 * endX;
      const py = mt3 * startY + 3 * mt2 * t * cp1y + 3 * mt * t2 * cp2y + t3 * endY;

      graphics.lineTo(px, py);
    }
  }

  /**
   * Draw a stroked path (for stems) with the specified color.
   */
  private drawColoredStrokePath(
    graphics: Phaser.GameObjects.Graphics,
    pathStr: string,
    color: number,
    lineWidth: number,
  ): void {
    if (!pathStr || pathStr.length === 0) return;

    graphics.lineStyle(lineWidth, color, 1);
    graphics.beginPath();

    let currentX = 0,
      currentY = 0;
    let startX = 0,
      startY = 0;

    const regex = /([MLQCZmlqcz])([^MLQCZmlqcz]*)/g;
    let match;

    while ((match = regex.exec(pathStr)) !== null) {
      const cmd = match[1];
      const params = match[2]
        .trim()
        .split(/[\s,]+/)
        .filter((s) => s.length > 0)
        .map(parseFloat);

      switch (cmd) {
        case 'M':
          if (params.length >= 2) {
            currentX = params[0];
            currentY = params[1];
            startX = currentX;
            startY = currentY;
            graphics.moveTo(currentX, currentY);
          }
          break;
        case 'm':
          if (params.length >= 2) {
            currentX += params[0];
            currentY += params[1];
            startX = currentX;
            startY = currentY;
            graphics.moveTo(currentX, currentY);
          }
          break;
        case 'L':
          if (params.length >= 2) {
            currentX = params[0];
            currentY = params[1];
            graphics.lineTo(currentX, currentY);
          }
          break;
        case 'l':
          if (params.length >= 2) {
            currentX += params[0];
            currentY += params[1];
            graphics.lineTo(currentX, currentY);
          }
          break;
        case 'Z':
        case 'z':
          graphics.closePath();
          currentX = startX;
          currentY = startY;
          break;
      }
    }

    graphics.strokePath();
  }

  /**
   * Get bounds of a path string.
   */
  private getPathBounds(pathStr: string): { x: number; y: number; width: number; height: number } {
    // Parse path to find bounds - handle all SVG path commands
    const regex = /([MLHVCSQTAZ])([^MLHVCSQTAZ]*)/gi;
    let match;
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    let currentX = 0,
      currentY = 0;

    while ((match = regex.exec(pathStr)) !== null) {
      const type = match[1].toUpperCase();
      const params = match[2]
        .trim()
        .split(/[\s,]+/)
        .filter((s) => s.length > 0)
        .map(parseFloat);

      switch (type) {
        case 'M':
        case 'L':
          if (params.length >= 2) {
            currentX = params[0];
            currentY = params[1];
            minX = Math.min(minX, currentX);
            maxX = Math.max(maxX, currentX);
            minY = Math.min(minY, currentY);
            maxY = Math.max(maxY, currentY);
          }
          break;
        case 'H':
          if (params.length >= 1) {
            currentX = params[0];
            minX = Math.min(minX, currentX);
            maxX = Math.max(maxX, currentX);
          }
          break;
        case 'V':
          if (params.length >= 1) {
            currentY = params[0];
            minY = Math.min(minY, currentY);
            maxY = Math.max(maxY, currentY);
          }
          break;
        case 'Q':
          // Quadratic bezier: control point + endpoint
          for (let i = 0; i < params.length; i += 4) {
            if (i + 3 < params.length) {
              minX = Math.min(minX, params[i], params[i + 2]);
              maxX = Math.max(maxX, params[i], params[i + 2]);
              minY = Math.min(minY, params[i + 1], params[i + 3]);
              maxY = Math.max(maxY, params[i + 1], params[i + 3]);
              currentX = params[i + 2];
              currentY = params[i + 3];
            }
          }
          break;
        case 'C':
          // Cubic bezier: two control points + endpoint
          for (let i = 0; i < params.length; i += 6) {
            if (i + 5 < params.length) {
              minX = Math.min(minX, params[i], params[i + 2], params[i + 4]);
              maxX = Math.max(maxX, params[i], params[i + 2], params[i + 4]);
              minY = Math.min(minY, params[i + 1], params[i + 3], params[i + 5]);
              maxY = Math.max(maxY, params[i + 1], params[i + 3], params[i + 5]);
              currentX = params[i + 4];
              currentY = params[i + 5];
            }
          }
          break;
      }
    }

    return {
      x: minX === Infinity ? 0 : minX,
      y: minY === Infinity ? 0 : minY,
      width: maxX === -Infinity || minX === Infinity ? 0 : maxX - minX,
      height: maxY === -Infinity || minY === Infinity ? 0 : maxY - minY,
    };
  }

  /**
   * Calculate and store the bounds of the rendered element.
   * Call this after VexFlow has finished drawing to this container.
   */
  updateBounds(): void {
    // Phaser Graphics doesn't have getBounds() directly, but we can get
    // bounds from the container which includes all its children
    try {
      const containerBounds = this.container.getBounds();
      // Store the actual world bounds - needed for overlay positioning
      // Since graphics are drawn at absolute VexFlow coordinates within the container,
      // we need to use those coordinates for the overlay
      this.bounds = new Phaser.Geom.Rectangle(
        containerBounds.x - this.container.x, // Convert to container-local coordinates
        containerBounds.y - this.container.y,
        containerBounds.width,
        containerBounds.height,
      );
    } catch (_e) {
      // If getBounds fails (e.g., empty container), create a default small bounds
      this.bounds = new Phaser.Geom.Rectangle(0, 0, 10, 10);
    }
  }

  /**
   * Explicitly set the bounds for this note container.
   * Use this when you know the exact position from OSMD note data.
   */
  setBounds(x: number, y: number, width: number, height: number): void {
    this.bounds = new Phaser.Geom.Rectangle(x, y, width, height);
  }

  /**
   * Store a fill path (note head). Called by PhaserVexFlowContext.fill().
   */
  addFillPath(path: string): void {
    if (path && path.length > 0) {
      this.fillPaths.push(path);
    }
  }

  /**
   * Get stored fill paths.
   */
  getFillPaths(): string[] {
    return this.fillPaths;
  }

  /**
   * Store a stroke path (stem, beam, etc.). Called by PhaserVexFlowContext.stroke().
   */
  addStrokePath(path: string, lineWidth: number): void {
    if (path && path.length > 0) {
      this.strokePaths.push({ path, lineWidth });
    }
  }

  /**
   * Get stored stroke paths.
   */
  getStrokePaths(): { path: string; lineWidth: number }[] {
    return this.strokePaths;
  }

  /**
   * Get the cached bounds of the element.
   */
  getBounds(): Phaser.Geom.Rectangle | null {
    return this.bounds;
  }

  // ==================== Animation Methods ====================

  /**
   * Play a pulse/halo animation - a circle that expands outward from the note head and fades out.
   * This matches the animation style in SightReadingPhaser.ts Note.pulse()
   * If no color is provided, uses the correct color based on colorblind type.
   */
  pulse(color?: number, duration: number = 750): void {
    // Use colorblind-aware correct color if no color provided
    if (color === undefined) {
      const stateColors = getStateColors(this.colorblindType);
      color = stateColors.correct.fill;
    }
    // Get the center of the note head for the halo origin
    const center = this.getNoteHeadCenter();

    // Create or reuse the pulse circle
    if (!this.pulseObject) {
      this.pulseObject = this.scene.add.circle(center.x, center.y, 0, color, 1);
      this.container.add(this.pulseObject);
      // Send to back so it appears behind the note
      this.container.sendToBack(this.pulseObject);
    } else {
      // Reset for new pulse
      this.pulseObject.setPosition(center.x, center.y);
      this.pulseObject.setRadius(0);
      this.pulseObject.setAlpha(1);
      this.pulseObject.setFillStyle(color, 1);
    }

    // Stop any existing pulse tween
    if (this.pulseTween) {
      this.pulseTween.stop();
    }

    // Animate the circle expanding and fading (smaller radius for subtler effect)
    this.pulseTween = this.scene.tweens.add({
      targets: this.pulseObject,
      radius: 20,
      alpha: 0,
      duration: duration,
      ease: 'Power2',
      onComplete: () => {
        this.pulseObject?.setAlpha(0);
      },
    });

    this.activeTweens.push(this.pulseTween);
  }

  /**
   * Get the center position of the note head (for pulse animation origin).
   * Uses the first fill path bounds if available, otherwise container center.
   */
  private getNoteHeadCenter(): { x: number; y: number } {
    if (this.fillPaths.length > 0) {
      const bounds = this.getPathBounds(this.fillPaths[0]);
      return {
        x: bounds.x + bounds.width / 2,
        y: bounds.y + bounds.height / 2,
      };
    }
    // Fallback to container bounds center
    const containerBounds = this.container.getBounds();
    return {
      x: containerBounds.width / 2,
      y: containerBounds.height / 2,
    };
  }

  /**
   * Play a shake animation for wrong notes.
   */
  shake(intensity: number = 4, duration: number = 200): void {
    const originalX = this.container.x;

    const tween = this.scene.tweens.add({
      targets: this.container,
      x: originalX + intensity,
      duration: duration / 4,
      yoyo: true,
      repeat: 3,
      ease: 'Sine.inOut',
      onComplete: () => {
        this.container.x = originalX;
      },
    });

    this.activeTweens.push(tween);
  }

  /**
   * Play a scale pop animation for emphasis.
   */
  scalePop(maxScale: number = 1.15, duration: number = 150): void {
    const tween = this.scene.tweens.add({
      targets: this.container,
      scaleX: maxScale,
      scaleY: maxScale,
      duration: duration,
      yoyo: true,
      ease: 'Back.out',
    });

    this.activeTweens.push(tween);
  }

  /**
   * Clear any highlight/glow effect.
   */
  clearHighlight(): void {
    if (this.overlay) {
      this.overlay.destroy();
      this.overlay = null;
    }
  }

  // ==================== Utility Methods ====================

  /**
   * Stop all active animations and reset to default state.
   */
  reset(): void {
    // Stop all tweens
    for (const tween of this.activeTweens) {
      tween.stop();
    }
    this.activeTweens = [];

    // Clear overlay
    this.clearHighlight();

    // Clear colored head graphics
    if (this.coloredHeadGraphics) {
      this.coloredHeadGraphics.destroy();
      this.coloredHeadGraphics = null;
    }

    // Clean up evenodd images and their textures
    for (const { image, textureKey } of this.evenOddImages) {
      image.destroy();
      if (this.scene.textures.exists(textureKey)) {
        this.scene.textures.remove(textureKey);
      }
    }
    this.evenOddImages = [];

    // Clean up pulse animation
    if (this.pulseTween) {
      this.pulseTween.stop();
      this.pulseTween = null;
    }
    if (this.pulseObject) {
      this.pulseObject.destroy();
      this.pulseObject = null;
    }

    // Reset state
    this.state = 'default';
    this.container.setAlpha(1);
    this.container.setScale(1);
    this.container.setPosition(this.originalPosition.x, this.originalPosition.y);

    // Show all original content
    this.showOriginalContent();
  }

  /**
   * Destroy the container and clean up resources.
   */
  destroy(): void {
    this.reset();
    this.graphics.destroy();
    if (this.overlay) {
      this.overlay.destroy();
    }
    if (this.coloredHeadGraphics) {
      this.coloredHeadGraphics.destroy();
    }
    if (this.pulseObject) {
      this.pulseObject.destroy();
    }
    this.container.destroy();
  }
}
